import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { TrinitiTimePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Bed, Plus, Trash2, AlertTriangle, RefreshCw, Lock, History,
} from 'lucide-react';
import { SleepoverAdjustmentModal } from './SleepoverAdjustmentModal';
import { useAppDialog } from '@/hooks/useAppDialog';

// Shows: active-hours intervals, add form, read-only billing estimate,
// adjustment history, and a 409 → credit-note modal.
export function CalendarSleepoverPanel({ shift, onUpdate, canEdit }) {
  const { dangerConfirm } = useAppDialog();
  const [form, setForm] = useState({ start_time: '23:00', end_time: '23:30', reason: '' });
  const [adding, setAdding] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  // Phase 2C: adjustment modal state
  const [adjModal, setAdjModal] = useState({ open: false, mode: 'correct', interval: null, lockReason: null });
  // Adjustments history
  const [adjustments, setAdjustments] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const intervals = shift.active_hours || [];

  const fetchEstimate = async () => {
    setEstimateLoading(true);
    try {
      const res = await api.get(`/roster/shifts/${shift.id}/sleepover-estimate`);
      setEstimate(res.data?.data);
    } catch (e) {
      if (e.response?.status !== 403) {
        toast.error(e.response?.data?.detail || 'Failed to load estimate');
      }
    } finally { setEstimateLoading(false); }
  };

  const fetchAdjustments = async () => {
    try {
      const res = await api.get(`/roster/shifts/${shift.id}/adjustments`);
      setAdjustments(res.data?.data || []);
    } catch (e) {
      if (e.response?.status !== 403) {
        // non-fatal
      }
    }
  };

  useEffect(() => {
    fetchEstimate();
    fetchAdjustments();
    /* eslint-disable-next-line */
  }, [shift.id, intervals.length]);

  const isBillingLocked = (err) => {
    if (err?.response?.status !== 409) return false;
    const data = err.response?.data || {};
    // Platform wraps HTTPException as { error: { code, message, details } } (errors.py).
    // Some legacy responses still use { detail: {...} } — handle both shapes.
    const errorCode = data.error?.code || data.detail?.code;
    const errorMsg = String(data.error?.message || data.detail || '');
    return errorCode === 'SHIFT_BILLING_LOCKED' || errorMsg.includes('SHIFT_BILLING_LOCKED');
  };

  const extractLockReason = (err) =>
    err?.response?.data?.error?.details || err?.response?.data?.detail?.details || null;

  const handleAdd = async () => {
    if (!form.start_time || !form.end_time || !form.reason || form.reason.trim().length < 3) {
      toast.error('Provide start, end, and a reason (min 3 chars)');
      return;
    }
    setAdding(true);
    try {
      await api.post(`/roster/shifts/${shift.id}/active-hours`, form);
      toast.success('Active hours added');
      setForm({ start_time: '23:00', end_time: '23:30', reason: '' });
      onUpdate?.();
    } catch (e) {
      if (isBillingLocked(e)) {
        // Open the adjustment modal in ADD_AFTER_BILLING mode
        setAdjModal({
          open: true, mode: 'add',
          interval: { start_time: form.start_time, end_time: form.end_time, reason: form.reason },
          lockReason: extractLockReason(e),
        });
      } else {
        toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Failed to add');
      }
    } finally { setAdding(false); }
  };

  const handleRemove = async (iv) => {
    const ok = await dangerConfirm({
      title: 'Remove active-hours interval?',
      description: `Remove ${iv.start_time}–${iv.end_time}? If billing is already locked you'll be prompted to issue a credit note instead.`,
      confirmLabel: 'Remove interval',
      testId: 'sleepover-remove-interval-confirm',
    });
    if (!ok) return;
    try {
      await api.delete(`/roster/shifts/${shift.id}/active-hours/${iv.id}`);
      toast.success('Interval removed');
      onUpdate?.();
    } catch (e) {
      if (isBillingLocked(e)) {
        setAdjModal({
          open: true, mode: 'credit', interval: iv,
          lockReason: extractLockReason(e),
        });
      } else {
        toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Failed to remove');
      }
    }
  };

  const handleCorrect = (iv) => {
    // Explicit "correct" action (always prompts the modal in correction mode).
    setAdjModal({ open: true, mode: 'correct', interval: iv, lockReason: null });
  };

  return (
    <div className="space-y-3" data-testid="sleepover-panel">
      <Card className="border-slate-300 border-dashed">
        <CardHeader className="pb-2 px-3 pt-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Bed className="w-4 h-4 text-slate-700" /> Active Hours
            <Badge variant="outline" className="text-[9px] ml-auto">{intervals.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0 space-y-2">
          {intervals.length === 0 ? (
            <p className="text-xs text-muted-foreground">No active hours recorded for this sleep-over.</p>
          ) : (
            intervals.map((iv) => (
              <div key={iv.id} className="flex items-start justify-between bg-slate-50 rounded-md p-2 gap-2" data-testid={`active-interval-${iv.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono">{iv.start_time}–{iv.end_time}</p>
                  <p className="text-[10px] text-muted-foreground line-clamp-2">{iv.reason}</p>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-0.5">
                    <Button
                      size="sm" variant="ghost"
                      className="h-6 px-1 text-slate-500 hover:text-primary"
                      title="Correct (credit-note + debit pair)"
                      onClick={() => handleCorrect(iv)}
                      data-testid={`correct-interval-${iv.id}`}
                    >
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="h-6 px-1 text-red-500"
                      onClick={() => handleRemove(iv)}
                      data-testid={`remove-interval-${iv.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
          {canEdit && (
            <div className="space-y-1.5 border-t pt-2 mt-2">
              <Label className="text-[10px] font-medium">Add interval</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Start</Label>
                  <Label className="text-xs">Active Hours Start</Label>
                  <TrinitiTimePicker value={form.start_time} onChange={v => setForm({ ...form, start_time: v })} testId="active-hours-start" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">End</Label>
                  <Label className="text-xs">Active Hours End</Label>
                  <TrinitiTimePicker value={form.end_time} onChange={v => setForm({ ...form, end_time: v })} testId="active-hours-end" />
                </div>
              </div>
              <Input className="h-7 text-xs" placeholder="Reason (e.g. medication wake-up)" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} data-testid="active-hours-reason" />
              <Button size="sm" onClick={handleAdd} disabled={adding} className="w-full h-7 text-xs" data-testid="active-hours-add">
                <Plus className="w-3 h-3 mr-1" /> {adding ? 'Adding...' : 'Add Active Hours'}
              </Button>
              <p className="text-[9px] text-muted-foreground">
                If the shift is already invoiced, this will open the credit-note flow instead.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Adjustments history */}
      {adjustments.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/30" data-testid="sleepover-adjustments-history">
          <CardHeader className="pb-2 px-3 pt-3">
            <CardTitle className="text-sm flex items-center gap-2 text-blue-900">
              <History className="w-4 h-4" /> Adjustments
              <Badge variant="outline" className="text-[9px] ml-auto bg-blue-100">{adjustments.length}</Badge>
              <Button
                size="sm" variant="ghost" className="h-5 px-1 text-[10px]"
                onClick={() => setShowHistory(v => !v)}
                data-testid="adjustments-toggle"
              >
                {showHistory ? 'Hide' : 'Show'}
              </Button>
            </CardTitle>
          </CardHeader>
          {showHistory && (
            <CardContent className="p-3 pt-0 space-y-1.5 text-xs">
              {adjustments.map(adj => (
                <div key={adj.id} className="bg-white rounded p-2 border border-blue-100" data-testid={`adjustment-${adj.id}`}>
                  <div className="flex items-center gap-1 text-[10px]">
                    <Badge className={
                      adj.kind === 'CORRECTION' ? 'bg-indigo-100 text-indigo-700 border-indigo-300' :
                      adj.kind === 'CREDIT_ONLY' ? 'bg-red-100 text-red-700 border-red-300' :
                      'bg-green-100 text-green-700 border-green-300'
                    }>
                      {adj.kind}
                    </Badge>
                    <Badge variant="outline" className="text-[9px]">{adj.status}</Badge>
                    <span className="ml-auto text-muted-foreground font-mono">
                      {(adj.created_at || '').slice(0, 16)}
                    </span>
                  </div>
                  <p className="text-[11px] mt-1.5 italic text-muted-foreground">"{adj.adjustment_reason}"</p>
                  <div className="flex items-center justify-between mt-1 text-[10px] font-mono">
                    <span>
                      {adj.original_interval && (
                        <span>{adj.original_interval.start_time}–{adj.original_interval.end_time}</span>
                      )}
                      {adj.original_interval && adj.new_interval && <span className="mx-1">→</span>}
                      {adj.new_interval && (
                        <span className="font-semibold">{adj.new_interval.start_time}–{adj.new_interval.end_time}</span>
                      )}
                    </span>
                    <span className={`font-bold ${(adj.net_amount || 0) < 0 ? 'text-red-700' : 'text-green-700'}`}>
                      {(adj.net_amount || 0) >= 0 ? '+' : ''}${(adj.net_amount || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}

      <Card className="border-amber-300 bg-amber-50/40">
        <CardHeader className="pb-2 px-3 pt-3">
          <CardTitle className="text-sm flex items-center gap-2 text-amber-900">
            Billing Estimate <Badge className="bg-amber-200 text-amber-900 border-amber-400 text-[9px]">PREVIEW</Badge>
          </CardTitle>
          <p className="text-[10px] text-amber-800 italic">Estimate only — compare to invoiced total in the Comparison report.</p>
        </CardHeader>
        <CardContent className="p-3 pt-0 space-y-1.5 text-xs" data-testid="sleepover-estimate">
          {estimateLoading ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : !estimate ? (
            <p className="text-muted-foreground">Estimate unavailable.</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Flat rate ({estimate.flat_rate.code || '—'})</span>
                <span className="font-mono font-semibold" data-testid="estimate-flat">${estimate.flat_rate.amount.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  Active hours ({estimate.active_hours.total_billed_hours.toFixed(2)}h × ${estimate.active_hours.hourly_rate.toFixed(2)})
                </span>
                <span className="font-mono font-semibold" data-testid="estimate-active">${estimate.active_hours.amount.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between border-t pt-1.5 mt-1.5">
                <span className="font-semibold">Total Estimate</span>
                <span className="font-mono font-bold text-amber-900" data-testid="estimate-total">${estimate.total_estimate.toFixed(2)} AUD</span>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Min unit applied: {estimate.active_hours.applied_min_unit_hours}h · Mode: {estimate.active_hours.billing_mode}
              </p>
              {estimate.warnings?.length > 0 && (
                <Alert className="mt-2 border-amber-400 bg-amber-100/50 py-2">
                  <AlertTriangle className="w-3 h-3" />
                  <AlertDescription className="text-[10px] space-y-0.5" data-testid="estimate-warnings">
                    {estimate.warnings.map((w, i) => <p key={i}>• {w}</p>)}
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <SleepoverAdjustmentModal
        open={adjModal.open}
        onClose={() => setAdjModal({ ...adjModal, open: false })}
        onSuccess={() => { onUpdate?.(); fetchAdjustments(); }}
        shiftId={shift.id}
        mode={adjModal.mode}
        interval={adjModal.interval}
        lockReason={adjModal.lockReason}
      />
    </div>
  );
}
